import type { IconDefinition } from '@fortawesome/free-solid-svg-icons'

export class GridClass {
    public constructor(init?: Partial<GridClass>) {
        Object.assign(this, init)
        this._updatedAt = new Date()
    }
    defaultEmptyValue: string = '-'
    title?: string = ''
    subtitle?: string = ''
    cols: GridCol[] = []
    lines: GridLine[] = []
    showHeaders: boolean = true
    showLabels: boolean = true
    showActs: boolean = true
    showFilter: boolean = true
    showLines: boolean = true
    acts: GridButton[] = []
    _updatedAt: Date = new Date()
    fontSize?: string = null
    minWidth?: string = ''
    box: GridBox[] = []
}

export enum GridColTypeEnum {
    string,
    int,
    float,
    float_2, // 2 casas decimais
    date,
    button,
    badge,
    datetime,
    datetime_friendly,
    time,
    time_short,
    phone,
    email,
    toggle,
    zinput,
    percent,
    checkbox, // seleção de linha pra ações em massa — ver GridCol.bulkActions e GridLine.checked
}

export enum GridBadgeClassEnum {
    badge_alert = 'zbadge_alert',
    badge_danger = 'zbadge_danger',
    badge_success = 'zbadge_success',
    badge_default = 'zbadge_default',
    badge_blue = 'zbadge_blue',
    badge_purple = 'zbadge_purple',
    badge_disabled = 'zbadge_disabled',
}

export enum GridColAlignEnum {
    unset,
    left,
    center,
    right,
}

export enum GridItemShow {
    show,
    hide,
}

export class GridCol {
    public constructor(init?: Partial<GridCol>) {
        Object.assign(this, init)
    }
    size?: number = 0
    type?: GridColTypeEnum = GridColTypeEnum.string
    align?: GridColAlignEnum = GridColAlignEnum.unset
    class?: string = ''
    content: string = ''
    show?: boolean = true
    showTotal?: boolean = false
    mobile: GridItemShow = GridItemShow.show
    maxLength: number = null
    filter?: string[] = []
    showTotalBox?: boolean = false
    isGroup?: boolean = false
    // desktop: GridColShow = GridColShow.show
    // só pra type=checkbox: ações exibidas no menu do ícone de raio, no lugar do ordenar/filtrar do
    // cabeçalho. cada callback recebe um array com o "content" (ver GridSlot) de toda linha marcada.
    bulkActions?: GridButton[] = []
}

export class GridBox {
    label: string
    value: number
    decimals?: number
    class?: string = ''
}

export class GridLine {
    public constructor(init?: Partial<GridLine>) {
        Object.assign(this, init)
    }
    // usado só por exportGrid() (CSV) — não confundir com "checked" abaixo, que é a marcação do
    // checkbox de seleção em massa. são dois conceitos independentes de propósito.
    selected?: boolean = true
    slots: GridSlot[] = []
    visible?: boolean = true
    removed?: boolean = false
    class: string = ''
    // marcação da linha via coluna type=checkbox (ver GridColTypeEnum.checkbox)
    checked?: boolean = false
}

export class GridSlot {
    public constructor(init?: Partial<GridSlot>) {
        Object.assign(this, init)
    }
    content: any = null
    badge?: GridBadge = null
    // ícone/ação clicável exibido logo após o conteúdo (ex.: um "?" que abre um popover com mais
    // detalhes). só aparece no tipo padrão de célula (não em badge/button/toggle/zinput).
    action?: GridButton = null
    class?: string = ''
}

export class GridButton {
    public constructor(init?: Partial<GridButton>) {
        Object.assign(this, init)
    }
    label: string = ''
    title?: string = ''
    icon?: IconDefinition = null
    callback: Function | any = null
    class?: string = ''
    mobile?: GridItemShow = GridItemShow.show
    mobileShowLabel?: boolean = true
    disabled?: boolean = false
    color?: 'green' | 'red' | 'yellow' | 'secondary' | 'primary' = 'secondary'
}

export class GridToggle {
    public constructor(init?: Partial<GridToggle>) {
        Object.assign(this, init)
    }
    label: string = ''
    checked: boolean = false
    callback: Function = null
    class?: string = ''
    mobile?: GridItemShow = GridItemShow.show
    disabled?: boolean = false
}

export class GridZInput {
    public constructor(init?: Partial<GridZInput>) {
        Object.assign(this, init)
    }
    type: 'text' | 'number' | 'date' | 'select' | 'toggle' | 'multiselect' = 'text'
    label: string = ''
    options: { value: any; label: string }[] = []
    value: boolean = false
    callback: Function = null
    class?: string = ''
    mobile?: GridItemShow = GridItemShow.show
    disabled?: boolean = false
    help?: string = null
    // texto exibido com o campo vazio — ex.: o valor herdado quando o campo vazio significa "usa o padrão"
    placeholder?: string = ''
}

export class GridBadge {
    public constructor(init?: Partial<GridBadge>) {
        Object.assign(this, init)
    }
    label: string = ''
    color?: string = ''
    class?: GridBadgeClassEnum | string = GridBadgeClassEnum.badge_default
}

// item de Zdetails (z/zdetails.svelte) — uma exibição de dados no formato "rótulo + valor".
// reaproveita GridColTypeEnum/GridBadge pra manter o mesmo vocabulário de conteúdo do Zgrid
// (o próprio Zgrid usa esses mesmos tipos pra renderizar cada célula via zgrid_line.svelte,
// que é o componente compartilhado entre os dois — evita duplicar a lógica de renderização).
export class ZDetailItem {
    public constructor(init?: Partial<ZDetailItem>) {
        Object.assign(this, init)
    }
    label: string = ''
    type?: GridColTypeEnum = GridColTypeEnum.string
    content: any = null
    class?: string = ''
    maxLength?: number = null
    badge?: GridBadge = null
    action?: GridButton = null
    title?: string = ''
    size?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 = 6
    sizeM?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 = 12
    show?: boolean = true
}

// objeto de configuração de Zdetails (z/zdetails.svelte) — mesma lógica do GridClass para o Zgrid:
// um único objeto de entrada em vez de vários props soltos, pra dar espaço a novos parâmetros no
// futuro (ex.: título, ações) sem precisar mexer na assinatura do componente a cada novo campo.
export class DetailsClass {
    public constructor(init?: Partial<DetailsClass>) {
        Object.assign(this, init)
    }
    items: ZDetailItem[] = []
    defaultEmptyValue?: string = '-'
}
